<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type { NavigationContainerProps } from '@core/types'

const _props = defineProps<NavigationContainerProps>()
const props = useDefaults({ props: _props })

const { site } = useCoreConfig()
const menuLogo = useThemeLogo('menu')
const iconLogo = useThemeLogo('icon')
const { data: user, isLoggedIn, isExisting } = useUserInfo()
const { isVisible, resolveHref } = useNavigationFilter()
const { useLogout } = useUserAuthClient()
const { mutate: logout } = useLogout()
const { can } = useGuard()
const links = useLinks()
const { translations: promotionTranslations, pages } = usePromotionConfig()
const { useGetActiveRewards } = useRewardsClient()
const { data: activeRewards } = useGetActiveRewards({ enabled: isLoggedIn })
const bonusCount = computed(() => activeRewards.value?.length ?? 0)
const { scrollToSearch } = useGameSearch()
const { t } = useI18n()
const route = useRoute()
const { trackEvent } = useTracking()

const navigation = computed(() => {
    return (props.navigation ?? [])
        .filter(isVisible)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(link => ({
            active: route.path === link.href,
            name: link.href,
            path: link.href,
            title: link.title,
            external: link.external,
            href: resolveHref(link),
        }))
})

// Anchor for the desktop user-menu dropdown
const userMenuActivator = ref<ComponentPublicInstance | null>(null)

// User notifications
const [userNotificationsDrawerOpen, toggleUserNotificationsDrawer] = useToggle(false)

const { userNotifications, refresh: refreshNotificationQuery, markAsRead, useCallAction, getIcon, errors } = useUserNotifications({ enabled: isLoggedIn, exclude_expired: true, useBaseQueryKey: true })
const unreadNotificationsCount = computed(() => {
    return (userNotifications?.value ?? []).filter(notification => !notification.read).length
})

function goToNotificationsPage() {
    closeUserNotificationsDrawer()

    navigateTo(pages.notifications)
}

function closeUserNotificationsDrawer() {
    userNotificationsDrawerOpen.value = false
}

function toggleNotifications(userMenuOpen: boolean, toggleUserMenu: () => void): void {
    toggleUserNotificationsDrawer()
    if (userMenuOpen) {
        toggleUserMenu()
    }
}

const username = computed(() => user.value?.username ?? 'No username')
const displayName = computed(() => {
    const first = user.value?.firstName ?? ''
    const last = user.value?.lastName ?? ''
    return `${first} ${last}`.trim()
})

function handleBonusesClick() {
    trackEvent({
        event_name: 'site-nav_bonuses_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })

    return navigateTo(t('pages.myBonuses'))
}

async function handleDepositClick(event: MouseEvent) {
    trackEvent({
        event_name: 'site-nav_deposit_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })

    const isAllowed = await can()

    if (!isAllowed) {
        event.preventDefault()

        return
    }

    await navigateTo(t('pages.deposit'), { external: true })
}

function handleWithdrawalClick() {
    trackEvent({
        event_name: 'site-nav_withdrawal_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })

    return navigateTo(t('pages.withdraw'))
}

function handleMyAccountClick() {
    trackEvent({
        event_name: 'site-nav_my-account_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })
}

function handleSavedGamesClick() {
    trackEvent({
        event_name: 'site-nav_saved-games_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })
}

function handleSupportClick() {
    trackEvent({
        event_name: 'site-nav_support_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })
}

function handleLogoutClick() {
    trackEvent({
        event_name: 'site-nav_logout_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })

    logout()
}

const mutations = {
    callAction: useCallAction()
}

watch(() => userNotificationsDrawerOpen.value, (isOpen) => {
    if (!isOpen) {
        refreshNotificationQuery()
    }
})
</script>

<template>
    <UIPageNavigation
        class="nav"
        :navigation="navigation"
        :user-menu-enabled="isLoggedIn"
        @activate-search="scrollToSearch = true"
    >
        <template #brand>
            <NuxtLink
                :href="isExisting ? '/lobby' : '/'"
                :class="{ 'is-logged-in': isLoggedIn }"
            >
                <NuxtImg
                    v-if="menuLogo"
                    v-bind="menuLogo"
                    class="logo"
                    decoding="async"
                    loading="lazy"
                    preset="local"
                    provider="cloudflare"
                    :alt="`${site.title} logo`"
                />
                <NuxtImg
                    v-if="iconLogo"
                    v-bind="iconLogo"
                    class="logo--plain"
                    decoding="async"
                    loading="lazy"
                    preset="local"
                    provider="cloudflare"
                    :alt="`${site.title} logo`"
                />
            </NuxtLink>
        </template>

        <template #user-actions="{ state: { userMenuOpen }, methods: { toggleUserMenu, closeUserMenu } }">
            <template v-if="!isLoggedIn">
                <UIButton
                    v-bind="links.signup.props()"
                    id="nav-create-account-button"
                    :as="isExisting ? 'nav' : 'cta'"
                    size="sm"
                    data-test="navigation-container-button-signup"
                    :shiny="isExisting ? false : 'snake'"
                >
                    Join Now
                </UIButton>

                <UIButton
                    v-bind="links.login.props()"
                    id="nav-login-account-button"
                    :as="isExisting ? 'cta' : 'nav'"
                    size="sm"
                    data-test="navigation-container-button-login"
                >
                    Log In
                </UIButton>
            </template>

            <template v-else>
                <PaymentsDepositButton id="nav-deposit-button" />
                <UIDrawer
                    v-model="userNotificationsDrawerOpen"
                    :width="360"
                    :max-height="600"
                    :top="48"
                    @close="closeUserNotificationsDrawer"
                >
                    <template #activator>
                        <button
                            :class="[
                                'nav__notifications-menu-btn',
                                'nav__notifications-menu-btn-happytiger',
                                { 'nav__notifications-menu-btn--active': false, 'nav__notifications-menu-btn--unread': unreadNotificationsCount && userNotifications?.length },
                            ]"
                            data-test="label-open-notifications"
                            @click.stop="toggleNotifications(userMenuOpen, toggleUserMenu)"
                        >
                            <UIIcon
                                name="bell"
                                size="20"
                            />
                        </button>
                    </template>

                    <template #header>
                        <h2 class="nav__notifications-header">
                            Messages
                        </h2>
                    </template>

                    <template #body>
                        <template v-if="userNotifications?.length">
                            <PromotionUserNotificationCard
                                v-for="notification in userNotifications"
                                :key="notification.id"
                                :notification="notification"
                                :errors="errors"
                                :icon="getIcon(notification.type || 'bell')"
                                class="nav__notifications-card"
                                @call-action="mutations.callAction.mutate"
                                @mark-as-read="markAsRead.mutate({
                                    notifications: [notification]
                                })"
                            />
                        </template>
                        <template v-else>
                            <div class="nav-notifications__empty">
                                <div class="nav-notifications__empty-content">
                                    <p class="nav-notifications__empty-title">
                                        {{ promotionTranslations.notifications.noNotifications }}...
                                    </p>
                                    <span class="nav-notifications__empty-bar" aria-hidden="true" />
                                    <span class="nav-notifications__empty-bar nav-notifications__empty-bar--short" aria-hidden="true" />
                                </div>
                                <div class="nav-notifications__empty-icon">
                                    <UIIcon name="bell" size="24" />
                                </div>
                            </div>
                        </template>
                    </template>

                    <template v-if="userNotifications?.length" #footer>
                        <UIButton as="nav" class="nav__notifications-link" @click="goToNotificationsPage">
                            {{ promotionTranslations.notifications.seeAllNotifications }} ({{ userNotifications?.length }})
                        </UIButton>
                    </template>
                </UIDrawer>

                <UIUserAvatar
                    ref="userMenuActivator"
                    :username="username"
                    :display-name="displayName"
                    data-test="navigation-container-button-dropdown"
                    @click.stop="toggleUserMenu()"
                />

                <UIUserMenuPanel
                    :model-value="userMenuOpen"
                    :activator="userMenuActivator"
                    :username="username"
                    :display-name="displayName"
                    :bonus-count="bonusCount"
                    @update:model-value="(val: boolean) => !val && closeUserMenu()"
                    @deposit="handleDepositClick"
                    @bonuses="handleBonusesClick"
                    @logout="handleLogoutClick"
                >
                    <UIListLink
                        :href="t('pages.home')"
                        prepend-icon="partner-profile"
                        append-icon="arrow-right"
                        @click="handleMyAccountClick"
                    >
                        My Account
                    </UIListLink>
                    <UIListLink
                        :href="t('pages.savedGames')"
                        prepend-icon="saved-games"
                        append-icon="arrow-right"
                        @click="handleSavedGamesClick"
                    >
                        Saved games
                    </UIListLink>
                    <UIListLink
                        :href="t('pages.withdraw')"
                        prepend-icon="payout"
                        append-icon="arrow-right"
                        @click="handleWithdrawalClick"
                    >
                        Withdraw
                    </UIListLink>
                    <UIListLink
                        :href="t('pages.customerSupport')"
                        prepend-icon="support"
                        append-icon="arrow-right"
                        @click="handleSupportClick"
                    >
                        Support
                    </UIListLink>
                </UIUserMenuPanel>
            </template>
        </template>

        <template #user-menu>
            <UIListLink
                :href="t('pages.home')"
                class="nav__user-menu-link"
                prepend-icon="partner-profile"
                @click="handleMyAccountClick"
            >
                My Account
            </UIListLink>
            <UIListLink
                class="nav__user-menu-link"
                prepend-icon="credits"
                @click.prevent="handleDepositClick"
            >
                Deposit
            </UIListLink>
            <UIListLink
                :href="t('pages.withdraw')"
                class="nav__user-menu-link"
                prepend-icon="payout"
                @click="handleWithdrawalClick"
            >
                Withdraw
            </UIListLink>
            <UIListLink
                :href="t('pages.savedGames')"
                class="nav__user-menu-link"
                prepend-icon="saved-games"
                @click="handleSavedGamesClick"
            >
                Saved games
            </UIListLink>
            <UIListLink
                :href="t('pages.myBonuses')"
                class="nav__user-menu-link"
                prepend-icon="bonus"
                @click.prevent="handleBonusesClick"
            >
                My bonuses
            </UIListLink>
            <UIListLink
                :href="t('pages.customerSupport')"
                class="nav__user-menu-link"
                prepend-icon="support"
                @click="handleSupportClick"
            >
                Support
            </UIListLink>
            <UIListLink
                class="nav__user-menu-link"
                href="/users/logout"
                external
                prepend-icon="logout"
                data-cy="a-user-nav-logout"
                @click.prevent="handleLogoutClick"
            >
                Log out
            </UIListLink>
        </template>
    </UIPageNavigation>
</template>

<style lang="scss" scoped>
.nav {
    .nav__primary {
        gap: unset;
    }
}

.logo {
    display: block;

    &--plain {
        display: none;
    }
}

.is-logged-in {
    .logo {
        display: none;
        @media (min-width: $screen-sm) {
            display: block;
        }

        &--plain {
            display: block;
            max-width: 34px;

            @media (min-width: $screen-sm) {
                display: none;
            }
        }
    }
}

.nav__user-menu-btn,
.nav__notifications-menu-btn {
    display: flex;
    justify-items: center;
    aspect-ratio: 1 / 1;
    height: 36px;
    border-radius: $border-radius * 2;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.15);
    transition: box-shadow 0.2s ease-in-out;
    color: var(--link);
    background-color: var(--surface);

    &:hover {
        color: var(--link);
        text-decoration: unset;
        box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.17);
    }
}

.nav-notifications__empty {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 15px 25px;
    background-color: var(--notification-card-bg, var(--background));
    border-radius: var(--border-radius, $border-radius);

    &-content {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 10px;
        min-width: 0;
    }

    &-title {
        margin: 0;
        font-size: $font-size-base;
        color: var(--on-background, $notification-font-color);
    }

    &-bar {
        height: 8px;
        background-color: var(--on-background, $notification-font-color);
        border-radius: 4px;
        opacity: 0.2;

        &--short {
            width: 60%;
        }
    }

    &-icon {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        background-color: var(--notification-card-icon-bg, var(--surface));
        color: var(--notification-card-icon-color, var(--secondary));
        border-radius: 50%;
    }
}

.nav__notifications {
    &-menu-btn {
        display: grid;
        grid-template-rows: auto;
        grid-template-columns: 1fr;
        justify-items: center;
        align-items: center;
        color: $nav-icon-color;
        background-color: var(--surface);
        font-size: $font-size-xs;
        font-weight: $text-regular;
        border: none;
        white-space: nowrap;
        text-shadow: none;
        text-decoration: none;

        &::before {
            content: '';
            position: absolute;
            top: 3px;
            right: -2px;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background-color: transparent;
            transition: all 0.3s ease-in-out;
        }

        .icon {
            margin-left: 1px;
        }

        &--unread {
            position: relative;

            .icon {
                animation: wiggleScale 3.5s ease-in-out infinite;
            }

            &::before {
                content: '';
                position: absolute;
                top: -2px;
                right: -2px;
                width: 10px;
                height: 10px;
                border-radius: 50%;
                background-color: $notification-dot-color;
            }
        }

        &:hover,
        &:focus {
            color: $nav-icon-color-hover;
            background-color: $nav-button-color-hover;
        }
    }

    &-header {
        font-size: $font-size-lg;
        font-weight: $text-bold;
        padding: 0;
        margin: 0;
    }

    &-card {
        margin-bottom: 15px;

        &:last-of-type {
            margin-bottom: 0;
        }
    }

    &-link {
        display: flex;
        margin: 0 auto;
    }
}

@keyframes wiggleScale {
    0% {
        transform: rotate(0) scale(1);
    }
    4%,
    8% {
        transform: rotate(0) scale(1.1);
    }
    12% {
        transform: rotate(10deg) scale(1.1);
    }
    16% {
        transform: rotate(-7deg) scale(1.1);
    }
    20% {
        transform: rotate(5deg) scale(1.1);
    }
    24% {
        transform: rotate(-3deg) scale(1.1);
    }
    28% {
        transform: rotate(1deg) scale(1.1);
    }
    32%,
    36% {
        transform: rotate(0) scale(1.1);
    }
    40% {
        transform: rotate(0) scale(1);
    }
    100% {
        transform: rotate(0) scale(1);
    }
}

.nav__user-menu-link {
    padding: 0 15px;
    cursor: pointer;

    @media (min-width: $screen-md) {
        padding: 0 20px;
    }
}
</style>
